import { format } from "date-fns";
import "./PaymentTable.scss";
import NoPaymentsView from "./NoPaymentsView/NoPaymentsView";
import { Skeleton, Table, Text } from "@mantine/core";
import { statusBadge, refundButton, makePaymentButton } from "../../utils";
import { useStripePayments } from "@/utils/hooks/useStripePayments";
import { useState } from "react";
import { createCheckoutSession, createRefund } from "@/utils/api/payments";
import { useQueryClient } from "@tanstack/react-query";
import { showNotification } from "@mantine/notifications";

interface PaymentTableProps {
  isOwner?: boolean;
}

const PaymentTable = ({ isOwner = true }: PaymentTableProps) => {
  const params = { limit: 1000, offset: 0 };
  const { data, isLoading, error } = useStripePayments(params);
  const queryClient = useQueryClient();
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );

  let payments = data?.content?.paymentRequests ?? [];

  // Sort payments so that pending ones appear first
  payments = payments.sort((a: any, b: any) => {
    if (a.status === "pending" && b.status !== "pending") return -1;
    if (a.status !== "pending" && b.status === "pending") return 1;
    return 0;
  });

  const handleRefund = async (paymentRequestId: string) => {
    setLoadingStates((prev) => ({ ...prev, [paymentRequestId]: true }));
    try {
      await createRefund(paymentRequestId);

      queryClient.invalidateQueries({ queryKey: ["payments"] });
      showNotification({
        title: "Success",
        message: "Refund issued successfully",
        color: "teal",
      });
    } catch (err: any) {
      console.error("Error issuing refund:", err);
      showNotification({
        title: "Error",
        message: err?.message || "An error occurred while issuing the refund.",
        color: "red",
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [paymentRequestId]: false }));
    }
  };

  const handleMakePayment = async (paymentRequestId: string) => {
    setLoadingStates((prev) => ({ ...prev, [paymentRequestId]: true }));
    try {
      const response = await createCheckoutSession(paymentRequestId);
      const checkoutUrl = response?.content?.checkoutUrl;

      if (!checkoutUrl) {
        showNotification({
          title: "Error",
          message: "No checkout URL returned from the server.",
          color: "red",
        });
        setLoadingStates((prev) => ({ ...prev, [paymentRequestId]: false }));
        return;
      }

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (err: any) {
      console.error("Error creating checkout session:", err);
      showNotification({
        title: "Error",
        message:
          err?.message ||
          "An error occurred while creating the checkout session.",
        color: "red",
      });
      setLoadingStates((prev) => ({ ...prev, [paymentRequestId]: false }));
    }
  };

  if (error) {
    return (
      <div className="error">{error.message || "Failed to load payments."}</div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton height={8} radius="xl" />
        <Skeleton height={8} mt={6} radius="xl" />
        <Skeleton height={8} mt={6} width="70%" radius="xl" />
      </div>
    );
  }

  const headerFields = [
    ...(isOwner ? [{ label: "Client Name", ariaLabel: "Client Name" }] : []),
    { label: "Amount", ariaLabel: "Amount" },
    { label: "Requested", ariaLabel: "Requested" },
    { label: "Status", ariaLabel: "Status" },
    { label: "Description", ariaLabel: "Description" },
    { label: "Actions", ariaLabel: "Actions" },
  ];

  const rows = payments.map((payment: any) => {
    const isPaymentLoading = loadingStates[payment.id] || false;

    const getActionButton = () => {
      if (isOwner && payment.status === "paid") {
        return refundButton(
          payment.status,
          payment.id,
          isPaymentLoading,
          handleRefund
        );
      } else if (!isOwner && payment.status === "pending") {
        return makePaymentButton(
          () => handleMakePayment(payment.id),
          isPaymentLoading
        );
      }
      return null;
    };

    return (
      <Table.Tr key={payment.id}>
        {isOwner && (
          <Table.Td>
            {`${payment.clientFirstName} ${payment.clientLastName}`}
          </Table.Td>
        )}
        <Table.Td>${payment.amount / 100}</Table.Td>
        <Table.Td>{format(new Date(payment.createdAt), "yyyy-MM-dd")}</Table.Td>
        <Table.Td>{statusBadge(payment.status)}</Table.Td>
        <Table.Td
          style={{
            maxWidth: "200px",
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          <Text lineClamp={3}>{payment.description}</Text>
        </Table.Td>
        <Table.Td>{getActionButton()}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <div className="payment-table-container">
      <Table>
        <Table.Thead>
          <Table.Tr>
            {headerFields.map(({ label, ariaLabel }) => (
              <Table.Th key={label}>
                <div className="payment-table-header-text">
                  <Text c="dimmed" fw={500} size="sm" aria-label={ariaLabel}>
                    {label}
                  </Text>
                </div>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {payments.length > 0 ? (
            rows
          ) : (
            <Table.Tr>
              <Table.Td
                colSpan={headerFields.length}
                className="no-payments-cell"
              >
                <NoPaymentsView isOwner={isOwner} />
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </div>
  );
};

export default PaymentTable;
